import { Cookie as CookieNames } from '@ps/web-core-enums';
import { LoginSource } from '@ps/web-core-store';
import { canUseDOM } from '@ps/web-core-utils';
import Cookies from 'universal-cookie';

import type { RequestError } from '@swf/core/errors/RequestError';
import type { EffectParams, RootState } from '@swf/core/store';
import { getState, select } from '@swf/core/store';

import type { AuthError } from '.';
import { loginError, loginSuccess } from './utils';
import { fetchJWT } from './verifySession';

const cookies = new Cookies();
const CLIENT_VERSION = '1.0';

export interface RegisterPayload {
  country: string;
  email: string;
  userId: string;
  password: string;
  marketingMailList: boolean;
  age: number;
  EULA: boolean;
  state?: string;
  fiscalCode?: string;
  fiscalCodeType?: string;
  dob?: Date;
  skipLogin?: boolean;
}

export const registrationRequest = async (
  request: EffectParams['request'],
  {
    country,
    state,
    email,
    userId,
    password,
    marketingMailList: mailListIn,
    fiscalCode,
    fiscalCodeType,
    dob,
    skipLogin = false
  }: RegisterPayload,
  rootState: RootState
) => {
  const affiliateUrl = `${rootState.location.origin}/?source=`;
  await fetch(affiliateUrl, { credentials: 'include' }).catch(() => undefined);
  const affInfo = cookies.get(CookieNames.AFFILIATE_INFO);
  const installId = select.session.getInstallId(rootState);
  const resolvedRegistrationBrand = select.localSettings.getRegistrationResolvedBrand(rootState);
  const isMobileEmbedded = select.device.isMobileEmbedded(rootState);
  const { rdn, clientType, type } = (canUseDOM && window?.starsweb?.app) || {};
  const timestamp = Math.floor(Date.now() / 1000).toString();

  const payload = {
    installId,
    country,
    state,
    email,
    userId,
    password,
    mailListIn,
    fiscalCode,
    fiscalCodeType,
    dob,
    skipLogin,
    ...(isMobileEmbedded
      ? {
          affInfoMobile: {
            infoRdn: rdn,
            infoVersion: CLIENT_VERSION,
            infoGuid: timestamp
          },
          appType: type,
          clientType
        }
      : { affInfo })
  };

  return request({
    service: 'registration.ram',
    body: payload,
    headers: { brand: resolvedRegistrationBrand.toString() }
  });
};

const register = async (
  payload: RegisterPayload,
  { dispatch, request, rootState }: EffectParams
) => {
  const { marketingMailList } = payload;

  try {
    const { body } = await registrationRequest(request, payload, rootState);

    const token = await fetchJWT(
      {
        token: body.token,
        signature: body.signature,
        webId: body.userWebId
      },
      request
    );

    await loginSuccess({ token, loginSource: LoginSource.Registration }, { dispatch });

    dispatch.auth.signEula({ marketingMailList, useMailList: true });

    // TODO move to loginSuccess https://jira.pyrsoftware.ca/jira/browse/WP-5186
    if (!select.account.shouldOpenRmf(getState(), LoginSource.Registration)) {
      dispatch.modal.enqueue({
        path: '/auth/registration/welcome',
        locationState: { marketingMailList }
      });
    }
  } catch (err) {
    loginError(payload, err as RequestError<AuthError>, dispatch);
    dispatch.modal.replace([{ path: '/auth/registration', locationState: payload }]);
  }

  return;
};

export default register;
